import { useState } from 'react';
import { Alert, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  fetchHumanRemovalPreview,
  removeGuildMember,
  removeHumanGuildMemberCascade,
  type HumanRemovalPreview,
} from '../lib/api';
import { routes } from '../lib/routes';
import { useAuthStore, useServerStore } from '../stores';

interface Props {
  userId: string;
  displayName: string;
  role: 'member' | 'agent';
  compact?: boolean;
}

// Server membership is managed where an owner is already looking at the
// member's profile. The server remains the authority for both the owner check
// and the destructive command; this control only exposes the existing flow.
export default function ServerMemberRemovalControl({ userId, displayName, role, compact = false }: Props) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  const guildId = useAuthStore((state) => state.guildId);
  const guilds = useServerStore((state) => state.guilds);
  const loadMembers = useServerStore((state) => state.loadMembers);
  const isServerOwner = !!guildId && guilds.some((guild) => guild.id === guildId && guild.owner_id === currentUser?.id);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<HumanRemovalPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  if (!guildId || !isServerOwner || userId === currentUser?.id) return null;

  const openConfirmation = () => {
    setOpen(true);
    setPreview(null);
    setError('');
    if (role !== 'member') return;
    setPreviewLoading(true);
    void fetchHumanRemovalPreview(guildId, userId)
      .then(setPreview)
      .catch(() => setError('Could not preview removal.'))
      .finally(() => setPreviewLoading(false));
  };

  const remove = async () => {
    try {
      setRemoving(true);
      if (role === 'member') await removeHumanGuildMemberCascade(guildId, userId);
      else await removeGuildMember(guildId, userId);
      await loadMembers(guildId);
      navigate(routes.members(guildId));
    } catch {
      setError('Could not remove member.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          className="icon-btn"
          title={`Remove ${displayName} from Project`}
          aria-label={`Remove ${displayName} from Project`}
          onClick={openConfirmation}
          style={{ color: 'var(--danger, #d32f2f)', fontSize: 18 }}
        >
          ×
        </button>
      ) : (
        <div className="agent-profile-field">
          <div className="agent-profile-field-label">PROJECT MEMBERSHIP</div>
          <button type="button" className="btn btn-sm btn-danger" onClick={openConfirmation}>
            Remove from Project
          </button>
        </div>
      )}
      <Modal
        open={open}
        title={`Remove ${displayName} from this Project?`}
        okText={role === 'member' ? 'Remove Human and access' : 'Remove Agent'}
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: removing || (role === 'member' && (!preview || previewLoading)) }}
        confirmLoading={removing}
        cancelButtonProps={{ disabled: removing }}
        closable={!removing}
        keyboard={!removing}
        mask={{ closable: !removing }}
        onOk={() => void remove()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
        width={520}
      >
        {role === 'agent' ? (
          <p>This removes only this Agent's Project membership.</p>
        ) : previewLoading ? (
          <p>Loading removal impact…</p>
        ) : preview ? (
          <>
            <p>This also removes this Human's directly owned Agents from this Project.</p>
            <ul>
              {preview.agents.map((agent) => (
                <li key={agent.user_id}>
                  <strong>{agent.display_name}</strong>: {agent.channel_names.length ? agent.channel_names.join(', ') : 'no channel memberships'}
                </li>
              ))}
            </ul>
            <p>{preview.channel_count} channel memberships and {preview.thread_count} thread memberships will be removed.</p>
          </>
        ) : (
          <p>Removal impact is unavailable.</p>
        )}
        {error && <Alert type="error" showIcon message={error} role="alert" />}
      </Modal>
    </>
  );
}
